import { motion } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Flame, Layers, ScanSearch } from "lucide-react";
import GradeSpecs from "@/components/GradeSpecs";

const IMG =
  "https://images.unsplash.com/photo-1474674556023-efef886fa147?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxtb2x0ZW4lMjBjb3BwZXIlMjBpbmR1c3RyaWFsJTIwbWV0YWwlMjBtYW51ZmFjdHVyaW5nJTIwd2FyZWhvdXNlfGVufDB8fHx8MTc4OTY2NzIxMXww&ixlib=rb-4.1.0&q=85";

const CARDS = [
  {
    icon: Flame,
    title: "Copper Scrap & Secondary Metals",
    body: "End-to-end command of physical cargo movement, grading standards, and refinery feeding requirements.",
    items: ["High-grade copper scrap: Millberry, Berry/Candy", "Heavy copper & wire: Birch/Cliff, Clove/Cobra", "Secondary copper & remelted copper ingots", "Copper Blister sourcing and trade flow"],
  },
  {
    icon: Layers,
    title: "Aluminum Alloys & Extrusions",
    body: "Trading and technical support for primary and secondary aluminum scrap fractions.",
    items: ["6063 aluminum extrusions", "Aluminum scrap ingot & secondary remelt", "Zorba non-ferrous fraction recovery", "HS Code 7601 & 7604 classification compliance"],
  },
  {
    icon: ScanSearch,
    title: "Sorting & Yield Optimization",
    body: "Modern sensor-based processing to maximize recovery rates and clean-grade yields.",
    items: ["X-ray Transmission (XRT) implementation", "X-ray Fluorescence (XRF) alloy characterization", "Density & sensor-based sorting strategies", "Quality verification for cross-border shipments"],
  },
];

export default function Physical() {
  return (
    <section id="physical" data-testid="physical-section" className="py-20 lg:py-32 bg-[#0D1119] border-y border-[#283244]/60">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          index="/02"
          overline="Physical Metals & Scrap Trading"
          title="Deep operational expertise across international ISRI specifications and secondary processing."
          testId="physical-head"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-5">
            <motion.div
              data-testid="physical-image-frame"
              whileHover={{ scale: 0.985 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-md border border-[#283244] img-spotlight lg:sticky lg:top-28"
            >
              <img src={IMG} alt="Molten copper ladle crane in industrial plant" className="w-full h-[420px] lg:h-[620px] object-cover" loading="lazy" />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-[#F3B700]">Millberry Grade · Cu ≥ 99.9%</div>
                <div className="font-display text-lg font-bold text-[#F4F6F8] mt-1">From yard grading to refinery feed</div>
              </div>
              <span className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-[#E06D28] z-10" />
              <span className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-[#E06D28] z-10" />
            </motion.div>
          </Reveal>

          <div className="lg:col-span-7 flex flex-col gap-6">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div data-testid={`physical-card-${i}`} className="card-industrial p-7 lg:p-9 group">
                  <div className="flex items-start gap-5">
                    <span className="shrink-0 w-11 h-11 rounded-sm bg-[#E06D28]/10 border border-[#E06D28]/30 flex items-center justify-center text-[#F07D38] transition-transform duration-300 group-hover:scale-110">
                      <c.icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg sm:text-xl font-bold text-[#F4F6F8]">{c.title}</h3>
                      <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{c.body}</p>
                      <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        {c.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                            <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#E06D28] shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <GradeSpecs />
      </div>
    </section>
  );
}
